import { useEffect, useRef, useState } from "react";
import { STATUS_COLORS } from "./StatusBadge";
import { tr } from "@/lib/i18n";
import api from "@/lib/api";

function markupsToGeoJSON(markups) {
    return {
        type: "FeatureCollection",
        features: (markups || []).map((m) => ({
            type: "Feature",
            properties: { id: m.id, color: m.color || "#EF476F", ...(m.label ? { label: m.label } : {}) },
            geometry: m.geometry,
        })),
    };
}

function compLinksToGeoJSON(links) {
    return {
        type: "FeatureCollection",
        features: (links || []).filter((l) => l.lat && l.lng).map((l) => ({
            type: "Feature",
            properties: {
                id: l.id, color: l.source === "Fire & Emergency Services" ? "#EF476F" : "#FB8500",
                source: l.source, requirement: l.requirement || "", ref: l.ref || "", status: l.status, file_number: l.file_number || "",
            },
            geometry: { type: "Point", coordinates: [l.lng, l.lat] },
        })),
    };
}

function haversineM(coords) {
    let total = 0;
    const R = 6371000, rad = Math.PI / 180;
    for (let i = 1; i < coords.length; i++) {
        const [x1, y1] = coords[i - 1], [x2, y2] = coords[i];
        const dLat = (y2 - y1) * rad, dLng = (x2 - x1) * rad;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(y1 * rad) * Math.cos(y2 * rad) * Math.sin(dLng / 2) ** 2;
        total += 2 * R * Math.asin(Math.sqrt(a));
    }
    return total;
}

const formatDistance = (m) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`);

function renderDraft(map, coords) {
    const src = map.getSource("markup-draft");
    if (!src) return;
    const features = coords.map((c) => ({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: c } }));
    if (coords.length > 1) features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } });
    src.setData({ type: "FeatureCollection", features });
}

export function appsToGeoJSON(apps) {
    return {
        type: "FeatureCollection",
        features: (apps || [])
            .filter((a) => a.parcel && a.location)
            .map((a) => ({
                type: "Feature",
                properties: {
                    id: a.id,
                    file_number: a.file_number,
                    status: a.status,
                    title: a.title || "",
                    address: a.address || "",
                    type: a.type || "",
                    ward: a.ward || "",
                    color: STATUS_COLORS[a.status] || "#94A3B8",
                },
                geometry: { type: "Polygon", coordinates: [a.parcel] },
            })),
    };
}

// Synthetic ward boundaries around the demo city extent (identify/layer demo layer)
const WARD_COLORS = ["#00B4D8", "#06D6A0", "#FFB703", "#EF476F", "#7B61FF", "#0A2540"];
export function wardGeoJSON() {
    const cx = -79.45, cy = 43.725;
    const features = [];
    for (let i = 0; i < 6; i++) {
        const ox = (i % 3) * 0.052 - 0.078, oy = Math.floor(i / 3) * 0.04 - 0.02;
        features.push({
            type: "Feature",
            properties: { ward: `Ward ${i + 1}`, color: WARD_COLORS[i] },
            geometry: {
                type: "Polygon",
                coordinates: [[
                    [cx + ox, cy + oy], [cx + ox + 0.05, cy + oy],
                    [cx + ox + 0.05, cy + oy + 0.038], [cx + ox, cy + oy + 0.038], [cx + ox, cy + oy],
                ]],
            },
        });
    }
    return { type: "FeatureCollection", features };
}

const LAYER_DEFS = [
    { id: "parcels", label: "Application parcels", layers: ["parcels-fill", "parcels-line"] },
    { id: "labels", label: "File labels", layers: ["parcels-label"] },
    { id: "wards", label: "Ward boundaries", layers: ["wards-fill", "wards-line", "wards-label"] },
];

export default function MapPanel({ applications = [], onSelect, className = "", focusId = null, showControls = true, tools = false, applicationId = null }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const popupRef = useRef(null);
    const identifyRef = useRef(false);
    const [layers, setLayers] = useState({ parcels: true, labels: true, wards: false });
    const [identify, setIdentify] = useState(false);
    const [drawMode, setDrawMode] = useState(null);
    const [markups, setMarkups] = useState([]);
    const [compLinks, setCompLinks] = useState([]);
    const [showLinks, setShowLinks] = useState(true);
    const [showImported, setShowImported] = useState(false);
    const [gisFeatures, setGisFeatures] = useState(null);
    const draftRef = useRef([]);
    const drawModeRef = useRef(null);
    const saveRef = useRef(null);

    const loadMarkups = () => {
        if (!tools) return;
        const q = applicationId ? `?application_id=${applicationId}` : "";
        api.get(`/markups${q}`).then(({ data }) => setMarkups(data)).catch(() => {});
        api.get("/compliance-links").then(({ data }) => setCompLinks(applicationId ? data.filter((l) => l.application_id === applicationId) : data)).catch(() => {});
    };

    const saveMarkup = async (kind, coords, label) => {
        try {
            const geometry = kind === "point"
                ? { type: "Point", coordinates: coords[0] }
                : kind === "polygon"
                    ? { type: "Polygon", coordinates: [[...coords, coords[0]]] }
                    : { type: "LineString", coordinates: coords };
            await api.post("/markups", { application_id: applicationId, kind, geometry, label, color: kind === "measure" ? "#0A2540" : "#EF476F" });
            loadMarkups();
        } catch { /* staff-only surface */ }
    };

    useEffect(() => { saveRef.current = saveMarkup; });
    useEffect(() => { loadMarkups(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tools, applicationId]);

    useEffect(() => {
        drawModeRef.current = drawMode;
        const map = mapRef.current;
        if (map) {
            if (drawMode) map.doubleClickZoom.disable();
            else map.doubleClickZoom.enable();
        }
        if (!drawMode) { draftRef.current = []; if (map) renderDraft(map, []); }
    }, [drawMode]);

    useEffect(() => {
        identifyRef.current = identify;
    }, [identify]);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        const maplibregl = window.maplibregl;
        if (!maplibregl) return;
        const map = new maplibregl.Map({
            container: containerRef.current,
            style: {
                version: 8,
                glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
                sources: {
                    osm: {
                        type: "raster",
                        tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
                        tileSize: 256,
                        attribution: "© OpenStreetMap contributors",
                    },
                },
                layers: [
                    { id: "osm", type: "raster", source: "osm", paint: { "raster-saturation": -0.7, "raster-brightness-max": 0.92 } },
                ],
            },
            center: [-79.45, 43.725],
            zoom: 12.2,
            attributionControl: false,
        });
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
        map.on("load", () => {
            map.addSource("parcels", { type: "geojson", data: appsToGeoJSON(applications) });
            map.addLayer({
                id: "parcels-fill",
                type: "fill",
                source: "parcels",
                paint: { "fill-color": ["get", "color"], "fill-opacity": 0.45 },
            });
            map.addLayer({
                id: "parcels-line",
                type: "line",
                source: "parcels",
                paint: { "line-color": ["get", "color"], "line-width": 2 },
            });
            map.addLayer({
                id: "parcels-label",
                type: "symbol",
                source: "parcels",
                layout: { "text-field": ["get", "file_number"], "text-size": 10, "text-font": ["Open Sans Semibold"] },
                paint: { "text-color": "#0A2540", "text-halo-color": "#ffffff", "text-halo-width": 1.4 },
            });
            map.addSource("wards", { type: "geojson", data: wardGeoJSON() });
            map.addLayer({
                id: "wards-fill", type: "fill", source: "wards",
                layout: { visibility: "none" },
                paint: { "fill-color": ["get", "color"], "fill-opacity": 0.08 },
            });
            map.addLayer({
                id: "wards-line", type: "line", source: "wards",
                layout: { visibility: "none" },
                paint: { "line-color": ["get", "color"], "line-width": 1.5, "line-dasharray": [2, 1.5] },
            });
            map.addLayer({
                id: "wards-label", type: "symbol", source: "wards",
                layout: { visibility: "none", "text-field": ["get", "ward"], "text-size": 11, "text-font": ["Open Sans Semibold"] },
                paint: { "text-color": "#334155", "text-halo-color": "#ffffff", "text-halo-width": 1.2 },
            });
            map.addSource("markups", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addLayer({ id: "markups-fill", type: "fill", source: "markups", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": ["get", "color"], "fill-opacity": 0.18 } });
            map.addLayer({ id: "markups-line", type: "line", source: "markups", filter: ["!=", ["geometry-type"], "Point"], paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-dasharray": [2, 1.2] } });
            map.addLayer({ id: "markups-point", type: "circle", source: "markups", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 6, "circle-color": ["get", "color"], "circle-stroke-width": 2, "circle-stroke-color": "#ffffff" } });
            map.addLayer({
                id: "markups-label", type: "symbol", source: "markups", filter: ["has", "label"],
                layout: { "text-field": ["get", "label"], "text-size": 10, "text-font": ["Open Sans Semibold"], "text-offset": [0, 1.1] },
                paint: { "text-color": "#0A2540", "text-halo-color": "#ffffff", "text-halo-width": 1.4 },
            });
            map.addSource("markup-draft", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addLayer({ id: "markup-draft-line", type: "line", source: "markup-draft", filter: ["==", ["geometry-type"], "LineString"], paint: { "line-color": "#0A2540", "line-width": 2, "line-dasharray": [1, 1] } });
            map.addLayer({ id: "markup-draft-point", type: "circle", source: "markup-draft", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 4, "circle-color": "#0A2540" } });
            map.addSource("comp-links", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addLayer({ id: "comp-links-point", type: "circle", source: "comp-links", paint: { "circle-radius": 7, "circle-color": ["get", "color"], "circle-stroke-width": 2, "circle-stroke-color": "#ffffff", "circle-opacity": 0.9 } });
            map.on("click", "comp-links-point", (e) => {
                const p = e.features[0].properties;
                if (popupRef.current) popupRef.current.remove();
                popupRef.current = new maplibregl.Popup({ closeButton: true, maxWidth: "300px" })
                    .setLngLat(e.lngLat)
                    .setHTML(
                        `<div style="padding:12px 14px">
                            <div style="font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${p.color}">${p.source}${p.ref ? ` · ${p.ref}` : ""}</div>
                            <div style="font-size:12px;color:#334155;margin-top:4px">${p.requirement}</div>
                            <div style="font-size:11px;color:#64748B;margin-top:4px">${p.file_number} · ${p.status}</div>
                        </div>`
                    )
                    .addTo(map);
            });
            map.on("mouseenter", "comp-links-point", () => (map.getCanvas().style.cursor = "pointer"));
            map.on("mouseleave", "comp-links-point", () => (map.getCanvas().style.cursor = ""));
            map.on("click", (e) => {
                const mode = drawModeRef.current;
                if (!mode) return;
                const coord = [e.lngLat.lng, e.lngLat.lat];
                if (mode === "point") {
                    saveRef.current?.("point", [coord], "");
                    draftRef.current = [];
                    renderDraft(map, []);
                    return;
                }
                draftRef.current = [...draftRef.current, coord];
                renderDraft(map, draftRef.current);
            });
            map.on("dblclick", (e) => {
                const mode = drawModeRef.current;
                if (!mode || mode === "point") return;
                e.preventDefault();
                const coords = draftRef.current;
                if (coords.length < 2) return;
                const label = mode === "measure" ? formatDistance(haversineM(coords)) : "";
                saveRef.current?.(mode, coords, label);
                draftRef.current = [];
                renderDraft(map, []);
            });
            map.addSource("gis-import", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addLayer({ id: "gis-import-fill", type: "fill", source: "gis-import", filter: ["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]], paint: { "fill-color": "#FB8500", "fill-opacity": 0.12 } });
            map.addLayer({ id: "gis-import-line", type: "line", source: "gis-import", filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString", "Polygon", "MultiPolygon"]]], paint: { "line-color": "#FB8500", "line-width": 1.6, "line-dasharray": [3, 2] } });
            map.addLayer({ id: "gis-import-point", type: "circle", source: "gis-import", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 5, "circle-color": "#FB8500", "circle-stroke-width": 1.5, "circle-stroke-color": "#ffffff" } });
            map.on("click", "gis-import-point", (e) => showPropsPopup(e));
            map.on("click", "gis-import-fill", (e) => showPropsPopup(e));
            map.on("click", "gis-import-line", (e) => showPropsPopup(e));
            function showPropsPopup(e) {
                if (drawModeRef.current) return;
                const p = e.features[0].properties;
                const rows = Object.entries(p).filter(([k]) => !k.startsWith("_")).slice(0, 8)
                    .map(([k, v]) => `<div style="font-size:11px;color:#334155"><b style="color:#0A2540">${k}</b>: ${v}</div>`).join("");
                if (popupRef.current) popupRef.current.remove();
                popupRef.current = new maplibregl.Popup({ closeButton: true, maxWidth: "300px" })
                    .setLngLat(e.lngLat)
                    .setHTML(`<div style="padding:12px 14px"><div style="font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#FB8500">Imported layer · ${p._layer}</div>${rows || '<div style="font-size:11px;color:#334155">No attributes</div>'}</div>`)
                    .addTo(map);
            }
            map.on("click", "parcels-fill", (e) => {
                const f = e.features[0];
                const p = f.properties;
                if (popupRef.current) popupRef.current.remove();
                if (identifyRef.current) {
                    // Identify mode — rich info popup, no navigation
                    popupRef.current = new maplibregl.Popup({ closeButton: true, maxWidth: "300px" })
                        .setLngLat(e.lngLat)
                        .setHTML(
                            `<div style="padding:12px 14px">
                                <div style="font-family:'JetBrains Mono',monospace;font-size:11px;color:#64748B">${p.file_number}</div>
                                <div style="font-family:'Outfit',sans-serif;font-weight:600;font-size:14px;color:#0A2540;margin:2px 0">${p.title}</div>
                                <div style="font-size:12px;color:#475569">${p.address}</div>
                                <div style="font-size:11px;color:#64748B;margin-top:4px">${p.type}${p.ward ? ` · ${p.ward}` : ""}</div>
                                <div style="margin-top:6px"><span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:99px;background:${p.color}22;color:${p.color}">${p.status}</span></div>
                            </div>`
                        )
                        .addTo(map);
                    return;
                }
                popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: "280px" })
                    .setLngLat(e.lngLat)
                    .setHTML(
                        `<div style="padding:12px 14px">
                            <div style="font-family:'JetBrains Mono',monospace;font-size:11px;color:#64748B">${p.file_number}</div>
                            <div style="font-family:'Outfit',sans-serif;font-weight:600;font-size:14px;color:#0A2540;margin:2px 0">${p.title}</div>
                            <div style="font-size:12px;color:#475569">${p.address}</div>
                            <div style="margin-top:6px"><span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:99px;background:${p.color}22;color:${p.color}">${p.status}</span></div>
                        </div>`
                    )
                    .addTo(map);
                if (onSelect) onSelect(p.id);
            });
            map.on("mouseenter", "parcels-fill", () => (map.getCanvas().style.cursor = "pointer"));
            map.on("mouseleave", "parcels-fill", () => (map.getCanvas().style.cursor = ""));
        });
        mapRef.current = map;
        return () => {
            map.remove();
            mapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const update = () => {
            const src = map.getSource("parcels");
            if (src) src.setData(appsToGeoJSON(applications));
        };
        if (map.isStyleLoaded()) update();
        else map.once("load", update);
    }, [applications]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const apply = () => {
            for (const def of LAYER_DEFS) {
                const vis = layers[def.id] ? "visible" : "none";
                def.layers.forEach((l) => {
                    if (map.getLayer(l)) map.setLayoutProperty(l, "visibility", vis);
                });
            }
        };
        if (map.isStyleLoaded()) apply();
        else map.once("load", apply);
    }, [layers]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const update = () => {
            const src = map.getSource("markups");
            if (src) src.setData(markupsToGeoJSON(markups));
        };
        if (map.isStyleLoaded()) update();
        else map.once("load", update);
    }, [markups]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const update = () => {
            const src = map.getSource("comp-links");
            if (src) src.setData(showLinks ? compLinksToGeoJSON(compLinks) : { type: "FeatureCollection", features: [] });
        };
        if (map.isStyleLoaded()) update();
        else map.once("load", update);
    }, [compLinks, showLinks]);

    useEffect(() => {
        if (!tools || !showImported || gisFeatures) return;
        api.get("/gis/features").then(({ data }) => setGisFeatures(data)).catch(() => {});
    }, [tools, showImported, gisFeatures]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const update = () => {
            const src = map.getSource("gis-import");
            if (src) src.setData(showImported && gisFeatures ? gisFeatures : { type: "FeatureCollection", features: [] });
        };
        if (map.isStyleLoaded()) update();
        else map.once("load", update);
    }, [gisFeatures, showImported]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !focusId) return;
        const target = applications.find((a) => a.id === focusId);
        if (target?.location) {
            map.flyTo({ center: [target.location.lng, target.location.lat], zoom: 14.5, duration: 1200 });
        }
    }, [focusId, applications]);

    return (
        <div className={`relative overflow-hidden rounded-[8px] ${className}`} data-testid="map-panel">
            <div ref={containerRef} className="absolute inset-0" />
            {!window.maplibregl && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-sm text-slate-500">
                    Map engine loading…
                </div>
            )}
            {drawMode && (
                <div className="absolute bottom-3 left-3 z-10 bg-[#0A2540]/90 text-white text-[11px] font-bold px-3 py-2 rounded-[8px]" data-testid="map-draw-hint">
                    {drawMode === "point" ? tr("Click the map to drop a point") : tr("Click to add points · double-click to finish")}
                </div>
            )}
            {showControls && (
                <div className="absolute top-3 right-3 z-10 space-y-2" data-testid="map-layer-control">
                    <div className="bg-white/95 backdrop-blur rounded-[10px] shadow-lg border border-slate-200/70 p-2 space-y-1 w-44">
                        <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-slate-400 px-1.5 pt-0.5">{tr("Layers")}</div>
                        {LAYER_DEFS.map((l) => (
                            <button key={l.id} data-testid={`layer-toggle-${l.id}`} onClick={() => setLayers((s) => ({ ...s, [l.id]: !s[l.id] }))}
                                className={`w-full flex items-center gap-2 px-1.5 py-1 rounded-[6px] text-[11px] font-semibold transition-colors ${layers[l.id] ? "text-[#0092B0]" : "text-slate-400"}`}>
                                <span className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center text-[9px] ${layers[l.id] ? "bg-[#00B4D8] border-[#00B4D8] text-white" : "border-slate-300"}`}>{layers[l.id] ? "✓" : ""}</span>
                                {tr(l.label)}
                            </button>
                        ))}
                        <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-slate-400 px-1.5 pt-1.5 border-t border-slate-100">{tr("Mode")}</div>
                        <div className="flex gap-1 px-1.5 pb-0.5">
                            <button data-testid="map-mode-select" onClick={() => setIdentify(false)}
                                className={`flex-1 px-2 py-1 rounded-[6px] text-[11px] font-bold transition-colors ${!identify ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-500"}`}>{tr("Select")}</button>
                            <button data-testid="map-mode-identify" onClick={() => setIdentify(true)}
                                className={`flex-1 px-2 py-1 rounded-[6px] text-[11px] font-bold transition-colors ${identify ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-500"}`}>{tr("Identify")}</button>
                        </div>
                        {tools && (
                            <>
                                <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-slate-400 px-1.5 pt-1.5 border-t border-slate-100">{tr("Markup")}</div>
                                <div className="grid grid-cols-4 gap-1 px-1.5 pb-0.5" data-testid="map-markup-tools">
                                    {[["point", "Point"], ["line", "Line"], ["polygon", "Area"], ["measure", "Measure"]].map(([m, label]) => (
                                        <button key={m} data-testid={`map-draw-${m}`} onClick={() => setDrawMode(drawMode === m ? null : m)}
                                            className={`px-1 py-1.5 rounded-[6px] text-[10px] font-bold transition-colors ${drawMode === m ? "bg-[#EF476F] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{tr(label)}</button>
                                    ))}
                                </div>
                                <button data-testid="map-links-toggle" onClick={() => setShowLinks(!showLinks)}
                                    className={`w-full flex items-center gap-2 px-1.5 py-1 rounded-[6px] text-[11px] font-semibold transition-colors ${showLinks ? "text-[#EF476F]" : "text-slate-400"}`}>
                                    <span className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center text-[9px] ${showLinks ? "bg-[#EF476F] border-[#EF476F] text-white" : "border-slate-300"}`}>{showLinks ? "✓" : ""}</span>
                                    {tr("Compliance links")}
                                </button>
                                <button data-testid="map-imported-toggle" onClick={() => setShowImported(!showImported)}
                                    className={`w-full flex items-center gap-2 px-1.5 py-1 rounded-[6px] text-[11px] font-semibold transition-colors ${showImported ? "text-[#FB8500]" : "text-slate-400"}`}>
                                    <span className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center text-[9px] ${showImported ? "bg-[#FB8500] border-[#FB8500] text-white" : "border-slate-300"}`}>{showImported ? "✓" : ""}</span>
                                    {tr("Imported GIS layers")}
                                </button>
                                {markups.length > 0 && (
                                    <button data-testid="map-markup-clear" onClick={async () => { for (const m of markups) { await api.delete(`/markups/${m.id}`).catch(() => {}); } loadMarkups(); }}
                                        className="w-full px-1.5 py-1 rounded-[6px] text-[11px] font-semibold text-slate-400 hover:text-[#EF476F] transition-colors text-left">{tr("Clear markups")}</button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
